import { Daycare, ExpirationDuration } from '@/types/Daycare'
import {
    OccasionalSlot,
    OccasionalSlotIcon,
    OccasionalSlotPriorityChild,
    OccasionalSlotPriorityGroup
} from '@/types/Slots'
import moment, { Moment } from 'moment'
import { useState } from 'react'
import getInitialOpeningAndClosingTime from '../functions/getInitialOpeningAndClosingTime'
import { DateSelection } from '../../models/dateSelection'
import { DAY_FORMAT, DAYTIME_FORMAT, Expiry, HOUR_MINUTE_FORMAT } from '../../models/constants'
import { momentDay, momentDaytime } from '../../models/common'
import { OccasionalSlotFormProps } from '../types'

export function useOccasionalSlotFormState(props: OccasionalSlotFormProps, currentDaycare: Daycare) {
    const { type } = props
    const defaultExpirationDuration: ExpirationDuration = currentDaycare.expirationDuration

    const occasionalSlot: OccasionalSlot | undefined =
        type === 'edit' ? props.occasionalSlot : type === 'multiEdit' ? props.occasionalSlots[0] : undefined

    const [dateSelection, setDateSelection] = useState<DateSelection>(
        type === 'add'
            ? { mode: 'range', startDate: props.selectedDate, endDate: props.selectedDate }
            : {
                  mode: 'single',
                  date: occasionalSlot ? moment(occasionalSlot.date, 'DD-MM-YYYY').format('DD-MM-YYYY') : ''
              }
    )

    function getFirstDate(): string {
        switch (dateSelection.mode) {
            case 'range': {
                return dateSelection.startDate
            }
            case 'list': {
                return moment.min(dateSelection.dates.map(momentDay)).format(DAY_FORMAT)
            }
            case 'single': {
                return dateSelection.date
            }
        }
    }

    const firstDate: string = getFirstDate()

    const { openingTime, closingTime } = getInitialOpeningAndClosingTime(props, currentDaycare)

    const [startTime, setStartTime] = useState<string>(openingTime)
    const startTimeMoment: Moment = moment(startTime, HOUR_MINUTE_FORMAT)

    const fullStartDate: string = momentDay(firstDate)
        .set({ hour: startTimeMoment.hour(), minute: startTimeMoment.minute() })
        .format(DAYTIME_FORMAT)

    const [endTime, setEndTime] = useState<string>(closingTime)

    function getInitialSectionId(): number | undefined {
        if (occasionalSlot?.sectionId) return occasionalSlot.sectionId
        if (type === 'add' && currentDaycare.sections?.length === 1) return currentDaycare.sections[0].id
        return undefined
    }

    const [selectedSectionId, setSelectedSectionId] = useState<number | undefined>(getInitialSectionId)

    const [priorityGroupIds, setPriorityGroupIds] = useState<number[]>(
        occasionalSlot?.priorityGroup?.map((group: OccasionalSlotPriorityGroup) => group.id) ?? []
    )

    const [priorityChildIds, setPriorityChildIds] = useState<number[]>(
        occasionalSlot?.priorityChild?.map((child: OccasionalSlotPriorityChild) => child.id) ?? []
    )

    const [confirmedChildIds, setConfirmedChildIds] = useState<number[]>(
        occasionalSlot?.childId ? [occasionalSlot.childId] : []
    )

    const canSchedule: boolean = confirmedChildIds.length === 0

    const [occasionalSlotNumber, setOccasionalSlotNumber] = useState<number>(
        type === 'multiEdit' ? props.occasionalSlots.length : 1
    )

    const [expiration, setExpiration] = useState<Expiry>(
        type === 'add'
            ? { type: 'duration', expiryDuration: defaultExpirationDuration }
            : occasionalSlot?.expiry
              ? { type: 'date', expiryDate: momentDaytime(occasionalSlot.expiry).format(DAYTIME_FORMAT) }
              : { type: 'duration', expiryDuration: ExpirationDuration.expiration0 }
    )

    const [placeId, setPlaceId] = useState<number | undefined>(occasionalSlot?.placeId ?? undefined)
    const [internalNoteValue, setInternalNoteValue] = useState<string>(occasionalSlot?.internalNote || '')
    const [externalNoteValue, setExternalNoteValue] = useState<string>(occasionalSlot?.externalNote || '')
    const [externalIcon, setExternalIcon] = useState<OccasionalSlotIcon>(
        occasionalSlot?.externalIcon || OccasionalSlotIcon.None
    )

    const [schedule, setSchedule] = useState<string>(
        occasionalSlot?.scheduled &&
            canSchedule &&
            moment(occasionalSlot.scheduled, DAYTIME_FORMAT).isAfter(moment(), 'minutes')
            ? moment(occasionalSlot.scheduled, DAYTIME_FORMAT).format(DAYTIME_FORMAT)
            : ''
    )

    const placesStillAvailable: boolean = confirmedChildIds.length < occasionalSlotNumber

    return {
        occasionalSlot,
        dateSelection,
        setDateSelection,
        firstDate,
        startTime,
        setStartTime,
        endTime,
        setEndTime,
        fullStartDate,
        selectedSectionId,
        setSelectedSectionId,
        priorityGroupIds,
        setPriorityGroupIds,
        priorityChildIds,
        setPriorityChildIds,
        confirmedChildIds,
        setConfirmedChildIds,
        canSchedule,
        occasionalSlotNumber,
        setOccasionalSlotNumber,
        expiration,
        setExpiration,
        placeId,
        setPlaceId,
        internalNoteValue,
        setInternalNoteValue,
        externalNoteValue,
        setExternalNoteValue,
        externalIcon,
        setExternalIcon,
        schedule,
        setSchedule,
        placesStillAvailable
    }
}

export type OccasionalSlotFormState = ReturnType<typeof useOccasionalSlotFormState>
